import type { UserProfile } from '@/src/renderer/lib/db/models'

/** Stable id of the single local profile used in offline mode. */
export const OFFLINE_USER_ID = 'offline-user'

/**
 * Builds the local `offline-user` profile record persisted when the user
 * finishes the offline profile setup.
 *
 * Crucially includes the chosen `avatar_preset_id`: the previous inline
 * `db.userProfiles.put({...})` replaced the whole record without it, wiping
 * the avatar the user had just picked — so the Settings profile section
 * rendered the fallback as if no picture had been selected.
 *
 * @param now Injectable timestamp (defaults to `new Date()`) — keeps the
 *            builder pure/testable.
 */
export function buildOfflineUserProfile(
  fullName: string,
  avatarPresetId: string | null,
  now: Date = new Date(),
): UserProfile {
  return {
    id: OFFLINE_USER_ID,
    fullName,
    username: null,
    email: null,
    displayName: null,
    avatar: null,
    avatar_preset_id: avatarPresetId,
    usagePurposes: [], // Filled later by the purpose step
    userIntents: [], // Filled later by the intent step
    createdAt: now,
    updatedAt: now,
    lastModified: now,
    synced: false,
    syncedAt: null,
    preferences: {
      theme: 'system',
      language: 'de',
      notifications: true,
      sounds: true,
      autoSync: false,
      workingHours: { start: '09:00', end: '17:00' },
      pomodoroSettings: {
        workDuration: 25,
        shortBreakDuration: 5,
        longBreakDuration: 15,
        cyclesBeforeLongBreak: 4,
      },
    },
    statistics: {
      totalTasksCompleted: 0,
      totalTimeSpent: 0,
      currentStreak: 0,
      longestStreak: 0,
      lastActiveDate: null,
    },
  }
}
